import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { addToast, handleAxiosError } from 'core/notifications';
import i18n from 'i18next';
import { testConditions, testValidators } from 'core/forms';

import useConfirmAction from '../hooks/useConfirmAction';

import MultiModificationTrackingForm from '../components/multiModificationTrackingForm';

const MultiModificationTrackingFormContainer = ({
  Component = MultiModificationTrackingForm,
  componentClassName = '',
  confirmBody = '',
  headerElement = 'h3',
  getIsConfirmationRequired = null,
  getSchemas = null,
  hasTimestamp = false,
  model = {},
  onSubmit = async () => {},
  schemas = [],
  titleExtra = null,
}) => {
  const [currentModel, setCurrentModel] = useState(model);
  const currentSchemas = getSchemas?.(currentModel) || schemas;
  const [formState, setFormState] = useState({});
  const getConfirmation = useConfirmAction({ body: confirmBody });

  const handleCancel = () => {
    setCurrentModel(model);

    currentSchemas.forEach(({ schema }) => {
      Object.entries(schema).forEach(([key, value]) => {
        const field = formState[key];

        if (!field) return;

        const conditionProps = testConditions(key, value, model);
        const errorProps = testValidators(key, value, model, conditionProps);

        Object.assign(field, conditionProps, errorProps, { isDirty: false });
      });
    });
  };

  const handleDataErrors = (error) => {
    error.response.data?.errors?.forEach(({ message, source }) => {
      const field = formState[source.split('data.').pop()];

      if (field) {
        Object.assign(field, { error: message, hasError: true });
      }
    });

    addToast(i18n.t('form-errors'), 'warning');
  };

  const handleSubmit = async ({ modifiedModel }) => {
    if (getIsConfirmationRequired?.(modifiedModel) && !(await getConfirmation())) return;

    try {
      await onSubmit(modifiedModel);
    } catch (error) {
      (error.response?.status === 422 ? handleDataErrors : handleAxiosError)(error);
    }
  };

  const handleUpdate = (state) => {
    setCurrentModel((prevModel) => ({ ...prevModel, ...state.update }));
    setFormState(state.formState);
  };

  return (
    <Component
      componentClassName={componentClassName}
      hasTimestamp={hasTimestamp}
      headerElement={headerElement}
      model={model}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onUpdate={handleUpdate}
      schemas={currentSchemas}
      titleExtra={titleExtra}
    />
  );
};

MultiModificationTrackingFormContainer.propTypes = {
  Component: PropTypes.elementType,
  componentClassName: PropTypes.string,
  confirmBody: PropTypes.string,
  getIsConfirmationRequired: PropTypes.func,
  getSchemas: PropTypes.func,
  hasTimestamp: PropTypes.bool,
  headerElement: PropTypes.string,
  model: PropTypes.shape({}),
  onSubmit: PropTypes.func,
  schemas: PropTypes.arrayOf(PropTypes.shape({})),
  titleExtra: PropTypes.node,
};

export default MultiModificationTrackingFormContainer;
